import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Typography,
  Radio,
  Image,
  Dropdown,
  message,
} from "antd";
import {
  EditOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  DownOutlined,
} from "@ant-design/icons";
import "./NewEmailSetup.css";
import FloatLabel from "../FloatLabel";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import emailIcon from "../../Assets/EmailCapture/email-icon.png";
import { URL } from "../../helper/Mainurl";

const { Title, Paragraph } = Typography;

const SCHOOL_YEARS = Array.from(
  { length: 13 },
  (_, i) => `School Year ${i + 1}`
);

const StepThree = ({
  onNext,
  onClose,
  initialChildrenData = [],
  skipAndClose,
}) => {
  const [children, setChildren] = useState(
    initialChildrenData.map((child) => ({
      id: child.id,
      child_name: child.child_name,
      school_name: child.school_name,
      school_grade: child.school_grade,
    }))
  );
  const [deletedChildren, setDeletedChildren] = useState([]);

  const [editingChild, setEditingChild] = useState(null);
  const [newChild, setNewChild] = useState({
    child_name: "",
    school_name: "",
    school_grade: "",
    yearType: 0,
  });

  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [childToDelete, setChildToDelete] = useState(null);

  const handleEdit = (child) => {
    const isCustom = !SCHOOL_YEARS.includes(child.school_grade);
    setEditingChild(child);
    setNewChild({
      child_name: child.child_name,
      school_name: child.school_name,
      school_grade: child.school_grade,
      yearType: isCustom ? 1 : 0,
    });
  };

  const handleDelete = (id) => {
    setChildToDelete(id);
    setShowDeleteModal(true);
  };

  // const confirmDelete = () => {
  //   setChildren(children.filter((child) => child.id !== childToDelete));
  //   if (child?.id) {
  //     setDeletedChildren((prev) => [...prev, String(child.id)]);
  //   }
  //   if (editingChild?.id === childToDelete) setEditingChild(null);
  //   setShowDeleteModal(false);
  //   setChildToDelete(null);
  // };
  const confirmDelete = () => {
    const child = children.find((c) => c.id === childToDelete);
    if (child?.id) {
      setDeletedChildren((prev) => [...prev, String(child.id)]);
    }
    setChildren(
      children.filter(
        (child) =>
          child.id !== childToDelete && child.child_name !== childToDelete
      )
    );
    if (editingChild?.id === childToDelete) setEditingChild(null);
    setShowDeleteModal(false);
    setChildToDelete(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setChildToDelete(null);
  };

  const handleSave = () => {
    if (!editingChild && children.length >= 3) {
      message.error("You can only add up to 3 children.");
      return;
    }

    if (newChild.child_name.trim().length < 3) {
      message.error("Child’s name must be at least 3 characters long.");
      return;
    }

    // 🚨 Restrict to max 3 children

    const childData = {
      id: editingChild ? editingChild.id : null,
      child_name: newChild.child_name,
      school_name: newChild.school_name || null,
      school_grade: newChild.school_grade || null,
    };

    if (editingChild) {
      setChildren(
        children.map((child) =>
          child.id === editingChild.id ? { ...childData } : child
        )
      );
    } else {
      setChildren([...children, childData]);
    }

    setNewChild({
      child_name: "",
      school_name: "",
      school_grade: "",
      yearType: 0,
    });
    setEditingChild(null);
  };

  const schoolYearMenu = (
    <div
      className="max-h-48 overflow-y-auto bg-white rounded-md shadow-lg p-4 scrollbar-none"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      <Radio.Group
        onChange={(e) => {
          const value = e.target.value;
          setNewChild({
            ...newChild,
            school_grade: value,
            yearType: value === "Custom" ? 1 : 0,
          });
          setYearDropdownOpen(false);
        }}
        value={newChild.yearType === 1 ? "Custom" : newChild.school_grade}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {SCHOOL_YEARS.map((year) => (
          <Radio
            className="flex items-center text-[16px] font-medium text-gray-700 hover:text-[#9A4FE0]"
            key={year}
            value={year}
          >
            {year}
          </Radio>
        ))}
        {/* <Radio value="Custom">Custom</Radio> */}
      </Radio.Group>
    </div>
  );

  const handleSubmitToAPI = async (isSkipped = false) => {
    try {
      const token = localStorage.getItem("authToken");

      let updatedChildren = [...children];

      // ✅ If user has filled the form but not clicked "Add Another Child"
      if (!isSkipped && newChild.child_name.trim().length >= 3) {
        const childData = {
          id: editingChild ? editingChild.id : null,
          child_name: newChild.child_name,
          school_name: newChild.school_name || null,
          school_grade: newChild.school_grade || null,
        };

        if (editingChild) {
          updatedChildren = updatedChildren.map((child) =>
            child.id === editingChild.id ? { ...childData } : child
          );
        } else {
          updatedChildren.push(childData);
        }
      }

      let payload = {
        children_setup_status: isSkipped ? "skipped" : "completed",
      };

      if (!isSkipped) {
        const sanitizedChildren = updatedChildren.map(
          ({ id, school_grade, ...rest }) => {
            const gradeNumber = school_grade
              ? parseInt(school_grade.match(/\d+/)?.[0] || "0", 10)
              : null;
            return {
              ...(id ? { id } : {}),
              ...rest,
              school_grade: gradeNumber ? gradeNumber.toString() : null,
            };
          }
        );

        payload.children = sanitizedChildren;
        payload.deleted_children = deletedChildren;
      }

      const response = await fetch(`${URL}/v1/email-assistant/children-setup`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) throw new Error(result.message || "Failed to save");

      message.success(result.message || "Children saved successfully!");
      onNext();
    } catch (err) {
      console.error(err);
      message.error("Something went wrong while saving.");
    }
  };

  useEffect(() => {
    console.log("childrenchildren_three", children);
  }, [children]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "end" }}>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{ color: "#bbb" }}
        />
      </div>

      <div
        className="emailSetupStep1Cont"
        style={{ maxWidth: 500, margin: "0 auto", textAlign: "center" }}
      >
        <Image src={emailIcon} preview={false} alt="Email setup" />
        <Title level={3}>Fine-Tune Your Email Assistant</Title>

        {children.map((child) => (
          <div
            key={child.id || child.child_name}
            style={{
              marginBottom: 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button type="primary" onClick={() => handleEdit(child)}>
              {child.child_name} <EditOutlined />
            </Button>
            {/* <Button icon={<DeleteOutlined />} onClick={() => handleDelete(child.id)} style={{ marginLeft: 8 }} /> */}
            <Button
              className="deleteIcon"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(child.id)}
              style={{ marginLeft: 8 }}
            />
          </div>
        ))}

        <div style={{ textAlign: "left", marginTop: 24 }}>
          <FloatLabel label="Child's Name" value={newChild.child_name}>
            <Input
              value={newChild.child_name}
              onChange={(e) =>
                setNewChild({ ...newChild, child_name: e.target.value })
              }
              style={{ width: "100%" }}
              disabled={children.length >= 3 && !editingChild}
            />
          </FloatLabel>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            Minimum 3 characters
          </Paragraph>

          <FloatLabel label="School Name" value={newChild.school_name}>
            <Input
              value={newChild.school_name}
              onChange={(e) =>
                setNewChild({ ...newChild, school_name: e.target.value })
              }
              style={{ width: "100%" }}
              disabled={children.length >= 3 && !editingChild}
            />
          </FloatLabel>

          <div style={{ marginTop: 16 }}>
            <FloatLabel label="School Year" value={newChild.school_grade}>
              <Dropdown
                overlay={schoolYearMenu}
                trigger={["click"]}
                open={yearDropdownOpen}
                onOpenChange={(flag) => setYearDropdownOpen(flag)}
                disabled={children.length >= 3 && !editingChild}
                getPopupContainer={(trigger) => trigger.parentElement} // keeps dropdown within parent
              >
                <div
                  className={`ant-input customDropdownlabel ${
                    children.length >= 3 && !editingChild ? "disabled" : ""
                  }`}
                  style={{
                    padding: "4px 11px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  {newChild.school_grade || (
                    <span style={{ color: "#bfbfbf", visibility: "hidden" }}>
                      ""
                    </span>
                  )}
                  <DownOutlined style={{ float: "right", marginTop: 0 }} />
                </div>
              </Dropdown>
            </FloatLabel>
          </div>

          {newChild.yearType === 1 && (
            <div style={{ marginTop: 16 }}>
              <FloatLabel label="Custom Year" value={newChild.school_grade}>
                <Input
                  value={newChild.school_grade}
                  onChange={(e) =>
                    setNewChild({ ...newChild, school_grade: e.target.value })
                  }
                  style={{ width: "100%" }}
                />
              </FloatLabel>
            </div>
          )}
        </div>

        <div style={{ margin: "20px 0" }}>
          {editingChild ? (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              block
            >
              Save Child
            </Button>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
              }}
            >
              <span style={{ marginRight: 8, fontWeight: 500 }}>
                Add Another Child
              </span>
              <Button
                shape="circle"
                icon={<PlusOutlined />}
                size="small"
                style={{
                  backgroundColor: "#9A4FE0",
                  color: "white",
                  border: "none",
                  padding: "12px",
                  fontSize: "12px",
                }}
                onClick={handleSave}
                // disabled={children.length >= 3}
              />
            </div>
          )}
        </div>

        <Button
          className="setup-btn"
          type="primary"
          block
          onClick={() => handleSubmitToAPI(false)}
        >
          {/* {authorization_status[0].label_name} */} Save and Continue
        </Button>
        <Button
          className="modal-btn-skip"
          block
          style={{ marginTop: "10px" }}
          onClick={() => handleSubmitToAPI(true)}
        >
          Skip & Continue
        </Button>
      </div>

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default StepThree;
